import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiErrorEnum } from '@/types/api';
import { HEADER_HEIGHT } from '@/utils/explorer';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
} from '@/api/qa';
import {
  IOutcome,
  ITrainersData,
  ITrainersPage,
  ITrainersTool,
  IMosaicAnnotation,
  IAnnotations,
  ITrainerSubmit,
} from '@/types/trainers';
import ErrorMessage from './shared/ErrorMessage';
import TrainerHeader from './TrainerHeader';
import TrainerButton from './TrainerButton';
import TrainerFooter from './TrainerFooter';
import Feedback from './shared/Feedback';
import TrainerController from './TrainerController';

export interface IMosaic {
  [key: number]: boolean;
}

interface Props extends ITrainersPage {
  data?: ITrainersData;
  tool?: ITrainersTool;
}

const MAX_IMAGE_DIMENSIONS = 150;

export default function MosaicTrainer({
  data: initialData,
  page,
  name,
  description,
  location,
  tool,
}: Props) {
  const controllerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [data, setData] = useState<any | undefined>(initialData);
  const { image, leaseId = '' } = data?.content || {};
  const cols = data?.content?.cols || 0;
  const rows = data?.content?.rows || 0;
  const [annotations, setAnnotations] = useState<IMosaic>({});
  const [isBenchmark, setIsBenchmark] = useState(false);
  const buttons = tool?.args?.buttonList || [];
  const [contentHeight, setContentHeight] = useState(0);
  const [isDataReady, setIsDataReady] = useState(false);
  const { feedback, setFeedback } = useTrainerFeedback();
  const hasAnySelected = Object.values(annotations).some(
    annotationValue => annotationValue === false,
  );

  const { incrementReviews } = useTrainerReviews();

  const clearAllData = () => {
    setData(undefined);
    setIsBenchmark(false);
    setIsDataReady(false);
  };

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onBenchmarkToggle = (checked: boolean) => {
    setIsBenchmark(checked);
  };

  const onSubmit = async ({ annotations, outcome }: ITrainerSubmit) => {
    clearAllData();

    const sending = {
      leaseId,
      ...(annotations && { annotations }),
      ...(outcome && { outcome }),
    };
    if (isBenchmark) {
      await submitBenchmark(toolId, sending);
    }

    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      sending,
    );

    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }

    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };

  useEffect(() => {
    const calculateContentHeight = () => {
      const pageHeaderHeight = HEADER_HEIGHT;
      const sectionHeaderHeight = headerRef.current?.offsetHeight ?? 0;
      const controllerHeight = controllerRef.current?.offsetHeight ?? 0;
      const footerHeight = footerRef.current?.offsetHeight ?? 0;
      const newContentHeight =
        window.innerHeight -
        pageHeaderHeight -
        sectionHeaderHeight -
        controllerHeight -
        footerHeight;
      setContentHeight(newContentHeight);
    };

    calculateContentHeight();
    window.addEventListener('resize', calculateContentHeight);

    return () => window.removeEventListener('resize', calculateContentHeight);
  }, [leaseId]);

  useEffect(() => {
    if (data?.content?.rows && data?.content?.cols) {
      const newAnnotations: IMosaic = {};
      for (let i = 0; i < data.content.rows * data.content.cols; i++) {
        newAnnotations[i] = true;
      }
      setAnnotations(newAnnotations);
    }
  }, [data]);

  useEffect(() => {
    if (data && !data.error && !data?.content?.image) {
      const error = ApiErrorEnum.NoContent;
      setData({ error });
    }
  }, [data]);

  const setAnnotation = (index: number) => {
    setAnnotations(prev => {
      if (!prev) return prev;

      const newSelectorData = { ...prev };
      newSelectorData[index] = !newSelectorData[index];

      return newSelectorData;
    });
  };

  const renderRow = (rowIndex: number) => {
    if (!annotations) return null;

    const columnArray = [];
    for (let i = 0; i < cols; i++) {
      columnArray.push(i);
    }

    return (
      <div key={rowIndex} className="flex grow cursor-pointer">
        {columnArray.map((_, colIndex) => {
          const currentIndex = rowIndex * cols + colIndex;
          const isCurrentlySelected = annotations[currentIndex] === true;

          return (
            <div
              key={currentIndex}
              data-active={isCurrentlySelected}
              className="grow border-2 bg-neutral-0 data-[active=true]:bg-transparent"
              onClick={() => setAnnotation(rowIndex * cols + colIndex)}></div>
          );
        })}
      </div>
    );
  };

  const renderTable = () => {
    const rowArray = [];
    for (let i = 0; i < rows; i++) {
      rowArray.push(i);
    }
    return (
      <div
        className="absolute inset-0 flex flex-1 flex-col"
        style={{
          WebkitTapHighlightColor: 'transparent',
        }}>
        {rowArray.map((_, rowIndex) => {
          return renderRow(rowIndex);
        })}
      </div>
    );
  };

  const onImageLoaded = useCallback(() => {
    setIsDataReady(true);
  }, []);

  useEffect(() => {
    if (image) {
      // Create an image element and load the image asynchronously
      const _image = new Image();
      _image.src = image;

      // Wait for the image to load and get its dimensions
      _image.onload = () => {
        onImageLoaded();
      };
    }
  }, [image, onImageLoaded]);

  return (
    <div className="flex size-full flex-col items-center justify-start">
      <TrainerHeader
        data={data}
        name={name}
        description={description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <>
          <div
            className="relative flex size-full flex-1 flex-col items-center justify-center"
            style={{ height: contentHeight }}>
            <div
              className="relative w-full"
              style={{
                maxWidth: cols * MAX_IMAGE_DIMENSIONS,
                maxHeight: rows * MAX_IMAGE_DIMENSIONS,
              }}>
              {isDataReady ? (
                <>
                  <img
                    className="w-full"
                    src={image}
                    alt={'mosaic'}
                    loading="lazy"
                  />
                  {renderTable()}
                </>
              ) : null}
            </div>
          </div>
          <TrainerController
            isDataReady={isDataReady}
            data={data}
            containerRef={controllerRef}
            onSubmit={onSubmit}
            tool={tool}>
            {buttons.map((button: any) => {
              let label = button.label;
              if (button.value === 'submit' && hasAnySelected) {
                label = 'Submit';
              }
              return (
                <TrainerButton
                  key={button.value}
                  onClick={() => {
                    let outcome: IOutcome | undefined;
                    const finalAnnotations: IMosaicAnnotation[] = [];
                    if (button.value === 'skip') {
                      outcome = 'skip';
                    } else {
                      const keys = Object.keys(annotations);
                      keys.forEach(key => {
                        const index = Number(key);
                        if (
                          annotations[index] === button.isSubmissionInverted
                        ) {
                          finalAnnotations.push(index);
                        }
                      });
                    }
                    onSubmit({ annotations: finalAnnotations, outcome });
                  }}
                  label={label}
                />
              );
            })}
          </TrainerController>
        </>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={onBenchmarkToggle}
        isBenchmark={isBenchmark}
      />
      {feedback && <Feedback />}
    </div>
  );
}
